import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { FullPageSpinner } from "@/components/FullPageSpinner";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/auth/useAuth";
import { useRecordForm } from "@/hooks/useRecordForm";
import { DIRECTORY_WRITE_ROLES } from "./statuses";
import { ClientVisitsTab } from "../scheduling/ClientVisitsTab";
import { ClientCarePlansTab } from "../careplans/ClientCarePlansTab";

export function DirectoryFormPage({
  resource,
  statuses,
  blankValues,
  basePath,
  listLabel,
  newTitle,
  extraFields,
  statusHint,
}) {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const canEdit = hasRole(...DIRECTORY_WRITE_ROLES);
  const canDelete = Boolean(resource.remove);
  const [activeTab, setActiveTab] = useState("details");

  const {
    isNew,
    values,
    handleChange,
    isLoading,
    isSaving,
    isDirty,
    discard,
    error,
    fieldErrors,
    submit,
    remove,
  } = useRecordForm({ resource, id, blankValues, redirectTo: basePath });

  if (isLoading) {
    return <FullPageSpinner label="Loading record" />;
  }

  const heading = isNew ? newTitle : `${values.first_name} ${values.last_name}`.trim();
  const showClientTabs = !isNew && basePath === "/clients";
  const saveDisabled = isSaving || (!isNew && !isDirty);

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Delete this record permanently? This cannot be undone.",
    );
    if (confirmed) {
      await remove();
    }
  };

  const handleFormSubmit = (event) => {
    if (!isNew && !isDirty) {
      event.preventDefault();
      return;
    }
    submit(event);
  };

  const form = (
    <form onSubmit={handleFormSubmit} noValidate className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal details</CardTitle>
          <CardDescription>Name, status, and identity fields for this record.</CardDescription>
        </CardHeader>
        <CardContent>
          <fieldset disabled={!canEdit || isSaving} className="min-w-0 space-y-4 border-0 p-0">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" name="first_name" error={fieldErrors.first_name}>
                {(props) => (
                  <Input
                    {...props}
                    type="text"
                    value={values.first_name}
                    onChange={handleChange}
                    required
                  />
                )}
              </Field>
              <Field label="Last name" name="last_name" error={fieldErrors.last_name}>
                {(props) => (
                  <Input
                    {...props}
                    type="text"
                    value={values.last_name}
                    onChange={handleChange}
                    required
                  />
                )}
              </Field>
              {extraFields ? extraFields({ values, handleChange, fieldErrors }) : null}
              <Field label="Status" name="status" error={fieldErrors.status} hint={statusHint}>
                {(props) => (
                  <FormSelect
                    id={props.id}
                    name={props.name}
                    value={values.status}
                    onValueChange={(v) =>
                      handleChange({ target: { name: props.name, value: v } })
                    }
                    options={statuses}
                    placeholder="Select status…"
                    disabled={!canEdit || isSaving}
                  />
                )}
              </Field>
            </div>
          </fieldset>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact</CardTitle>
          <CardDescription>Phone and email used for outreach and scheduling.</CardDescription>
        </CardHeader>
        <CardContent>
          <fieldset disabled={!canEdit || isSaving} className="min-w-0 space-y-4 border-0 p-0">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone" name="phone" error={fieldErrors.phone}>
                {(props) => (
                  <Input {...props} type="tel" value={values.phone} onChange={handleChange} />
                )}
              </Field>
              <Field label="Email" name="email" error={fieldErrors.email}>
                {(props) => (
                  <Input {...props} type="email" value={values.email} onChange={handleChange} />
                )}
              </Field>
            </div>
          </fieldset>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Address</CardTitle>
          <CardDescription>Service and mailing address for this record.</CardDescription>
        </CardHeader>
        <CardContent>
          <fieldset disabled={!canEdit || isSaving} className="min-w-0 space-y-4 border-0 p-0">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Address line 1" name="address_line1" error={fieldErrors.address_line1}>
                {(props) => (
                  <Input
                    {...props}
                    type="text"
                    value={values.address_line1}
                    onChange={handleChange}
                  />
                )}
              </Field>
              <Field label="Address line 2" name="address_line2" error={fieldErrors.address_line2}>
                {(props) => (
                  <Input
                    {...props}
                    type="text"
                    value={values.address_line2}
                    onChange={handleChange}
                  />
                )}
              </Field>
              <Field label="City" name="city" error={fieldErrors.city}>
                {(props) => (
                  <Input {...props} type="text" value={values.city} onChange={handleChange} />
                )}
              </Field>
              <Field label="State" name="state" error={fieldErrors.state}>
                {(props) => (
                  <Input {...props} type="text" value={values.state} onChange={handleChange} />
                )}
              </Field>
              <Field label="Postal code" name="postal_code" error={fieldErrors.postal_code}>
                {(props) => (
                  <Input
                    {...props}
                    type="text"
                    value={values.postal_code}
                    onChange={handleChange}
                  />
                )}
              </Field>
            </div>
          </fieldset>
        </CardContent>
        {canEdit ? (
          <CardFooter className="flex flex-wrap items-center gap-2 border-t border-border px-4 pt-4">
            <Button type="submit" disabled={saveDisabled}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
            {!isNew ? (
              <Button
                type="button"
                variant="ghost"
                disabled={!isDirty || isSaving}
                onClick={discard}
              >
                Discard
              </Button>
            ) : null}
            <Button type="button" variant="ghost" asChild>
              <Link to={basePath}>Cancel</Link>
            </Button>
            {canDelete && !isNew ? (
              <Button
                type="button"
                variant="destructive"
                className="ml-auto"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Delete
              </Button>
            ) : null}
          </CardFooter>
        ) : (
          <CardFooter className="border-t border-border px-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Your role does not allow changes to the directory.
            </p>
          </CardFooter>
        )}
      </Card>
    </form>
  );

  return (
    <PageShell>
      <PageHeader
        title={heading || newTitle}
        description={
          <Link to={basePath} className="hover:underline">
            ← {listLabel}
          </Link>
        }
        actions={
          !isNew && basePath === "/caregivers" ? (
            <Button variant="ghost" asChild>
              <Link to={`/caregivers/${id}/schedule`}>View Schedule</Link>
            </Button>
          ) : null
        }
      />

      <PageBody>
        <Alert>{error}</Alert>
        <Alert tone="error">{fieldErrors.non_field_errors}</Alert>

        {showClientTabs ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="details">Client Profile</TabsTrigger>
              <TabsTrigger value="careplans">Care Plans</TabsTrigger>
              <TabsTrigger value="visits">Visits History</TabsTrigger>
            </TabsList>
            <TabsContent value="details">{form}</TabsContent>
            <TabsContent value="careplans">
              <ClientCarePlansTab clientId={id} />
            </TabsContent>
            <TabsContent value="visits">
              <ClientVisitsTab clientId={id} />
            </TabsContent>
          </Tabs>
        ) : (
          form
        )}
      </PageBody>
    </PageShell>
  );
}
